import MonthlyAiUsage from "../StitchEditor/MonthlyAiUsage";
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { 
  cloudUploadOutline,
  menuOutline,
  personOutline,
  logOutOutline,
  brush,
  schoolOutline,
  playCircleOutline,
  imageOutline,
  colorWandOutline,
  refreshOutline,
  cardOutline,
  shieldCheckmarkOutline,
  settingsOutline
} from 'ionicons/icons';
import SettingsModal from '../SettingsModal';
import SettingsPage from '../SettingsPage';
import SupportOpsPage from '../SettingsPage/SupportOpsPage';
import { Button, Upload } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExpand,
  faImages,
  faWandSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { AppContext } from '../../contexts/AppContext';
import { queueUploadImage } from '../../lib/upload/queueUploadImage';
import { useAppDispatch } from '../../redux/store';
import { useAuth } from '../../contexts/AuthContext';
import { signOutUser } from '../../lib/firebase/firebase';
import { isNativeiOS } from '../../lib/utils/platform';
import IOSPaywall from '../PaywallModal/IOSPaywall';
import { CancelInterceptModal } from '../CancelInterceptModal';
import { useTutorial } from '../Tutorial';
import { useTutorial as useTutorialProvider } from '../Tutorial/TutorialProvider';
import { PICK_DESIGN_STEP } from '../../tutorials';
import MobileSidebar from './MobileSidebar';
import { ReactComponent as SVGTraceLogo } from '../../assets/logo1.svg';
import TraceLogo from '../../assets/tracelogo.png';
import { features, isEmbroideryTrace, tutorialSampleImage, tutorialSampleFileName } from '../../config/appVariant';
import branding from '../../config/branding';
import './styles.css';

// Disabled sound effects - all sounds are muted
const playSound = (soundType) => {
  // All sounds are disabled - no audio will be played
  return;
};

// Sample image loading function
const loadSampleImage = async (imagePath = tutorialSampleImage) => {
  console.log('🎯 Loading sample image...');
  
  try {
    const sampleImagePath = imagePath;
    const response = await fetch(sampleImagePath);
    
    if (response.ok) {
      const blob = await response.blob();
      
      if (blob.size > 0) {
        const imageData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsDataURL(blob);
        });
        
        console.log('✅ Sample image loaded successfully');
        return imageData;
      }
    }
  } catch (error) {
    console.error('❌ Error loading sample image:', error);
  }
  
  // Fallback: Generate a simple sample image
  console.log('⚠️ Using fallback sample image');
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  
  // White background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 200, 200);
  
  // Draw a simple star
  ctx.fillStyle = '#667eea';
  ctx.beginPath();
  ctx.moveTo(100, 20);
  ctx.lineTo(120, 70);
  ctx.lineTo(180, 70);
  ctx.lineTo(135, 110);
  ctx.lineTo(155, 180);
  ctx.lineTo(100, 140);
  ctx.lineTo(45, 180);
  ctx.lineTo(65, 110);
  ctx.lineTo(20, 70);
  ctx.lineTo(80, 70);
  ctx.closePath();
  ctx.fill();
  
  return canvas.toDataURL('image/png');
};

const Sidebar = ({ isOpen, setIsOpen, onNavigate, onResizeImage, onBulkConvert, onGenerateImage, onSharpenImage, onModifyImage, onSvgFile, setImage64, setFileName, onBillingClick }) => {
  const [activeItem, setActiveItem] = useState('home');
  const [dragOver, setDragOver] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPersistentExpanded, setIsPersistentExpanded] = useState(() => window.innerWidth > 1024);
  const [soundsMuted, setSoundsMuted] = useState(true); // Always muted
  const [isMobile, setIsMobile] = useState(false);
  const [isCompact, setIsCompact] = useState(() => window.innerWidth > 768 && window.innerWidth <= 1024);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSettingsPage, setShowSettingsPage] = useState(false);
  const [showSupportOpsPage, setShowSupportOpsPage] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  const dispatch = useAppDispatch();
  const { setImageSource, tutorialMode } = useContext(AppContext);
  const { user, userIsPremium } = useAuth();
  const isSupportOpsAdmin =
    (user?.email || '').toLowerCase() === 'admin@svgtrace.com';
  const [iosPaywallAction, setIosPaywallAction] = useState(null);
  const { triggerAction, registerDesignLoader } = useTutorial();

  // On iOS: wrap any premium action so non-premium users see the feature-specific paywall
  const requirePremium = (featureName, action) => {
    if (isNativeiOS() && !userIsPremium) {
      setIosPaywallAction(featureName);
      return;
    }
    action();
  };
  const { startTutorial } = useTutorialProvider();

  useEffect(() => {
    const checkLayout = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 768);
      const tablet = w > 768 && w <= 1024;
      setIsCompact(tablet);
      setIsPersistentExpanded(w > 1024);
    };
    
    checkLayout();
    window.addEventListener('resize', checkLayout);
    
    return () => window.removeEventListener('resize', checkLayout);
  }, []);

  // The tutorial's design picker sits above the AppContext provider, so it has
  // no way to reach the real image setters. Lend it this component's.
  useEffect(() => {
    if (!registerDesignLoader) return undefined;

    registerDesignLoader(async (design) => {
      const imageData = await loadSampleImage(design.image);
      if (!imageData) throw new Error('Practice design could not be loaded');

      queueUploadImage(dispatch, imageData);
      // Name first: the embroidery fast path keys its pinned tutorial palette
      // off the file name, and setting the image is what kicks that path off.
      if (setFileName) setFileName(design.fileName);
      setImageSource('uploaded');
      if (setImage64) setImage64(imageData);
    });

    return () => registerDesignLoader(null);
  }, [registerDesignLoader, dispatch, setImage64, setFileName, setImageSource]);

  const handleSampleImageClick = useCallback(async () => {
    playSound('click');
    try {
      const imageData = await loadSampleImage();
      if (imageData) {
        queueUploadImage(dispatch, imageData);
        if (setImage64) {
          setImage64(imageData);
        }
        if (setFileName) {
          setFileName(tutorialSampleFileName);
        }
        setImageSource("uploaded");
        
        // Trigger tutorial action for file upload
        triggerAction('file-uploaded', { 
          fileName: tutorialSampleFileName, 
          fileType: 'image/png' 
        });
      }
    } catch (error) {
      console.error('Error loading sample image:', error);
    }
  }, [dispatch, setImage64, setFileName, setImageSource, triggerAction]);

  const sidebarItems = [
    {
      id: 'create',
      icon: cloudUploadOutline,
      label: 'Upload',
      isPrimary: true
    },
    // Hide "Use Sample Image" button when tutorial is active
    ...(!tutorialMode ? [{
      id: 'sample',
      icon: imageOutline,
      label: 'Use Sample Image',
      isPrimary: false,
      action: handleSampleImageClick
    }] : [])
  ];

  const handleItemClick = useCallback((item) => {
    playSound('click');
    setActiveItem(item.id);
    if (item.action) {
      // Execute custom action for items like sample image
      item.action();
    } else if (item.id !== 'create' && onNavigate) {
      onNavigate(item.id);
    }
  }, [onNavigate]);

  const handleItemHover = useCallback(() => {
    playSound('hover');
  }, []);

  const handleSidebarMouseEnter = useCallback(() => {
    setIsHovered(true);
    playSound('expand');
  }, []);

  const handleSidebarMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleFileUpload = (file) => {
    // Route SVGs through the dedicated SVG-import pipeline (detect compatibility
    // → direct-load or re-trace). See processSvgFile in Home/index.jsx.
    const isSvg =
      file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "");
    if (isSvg) {
      if (onSvgFile) onSvgFile(file);
      return false;
    }

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      console.log("Unsupported file format");
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      queueUploadImage(dispatch, e.target.result);
      if (setImage64) {
        setImage64(e.target.result);
      }
      if (setFileName) {
        setFileName(file.name);
      }
      
      // Trigger tutorial action for file upload
      triggerAction('file-uploaded', { 
        fileName: file.name, 
        fileType: file.type 
      });
    };
    setImageSource("uploaded");
    reader.readAsDataURL(file);
    return false;
  };

  const handleCustomerPortalClick = async () => {
    playSound('click');
    setShowCancelModal(true);
  };


  const handleSignOut = async () => {
    playSound('click');
    setSignOutLoading(true);
    try {
      await signOutUser();
    } catch (error) {
      console.log(error);
    } finally {
      setSignOutLoading(false);
    }
  };

  const handleTutorialClick = useCallback(() => {
    playSound('click');

    // Opens on the lesson list, like the Tutorials card on the home page. The
    // converter walkthrough has no lessons to choose between, so it starts at
    // the beginning.
    if (isEmbroideryTrace) {
      startTutorial('embroidery', PICK_DESIGN_STEP);
      return;
    }

    startTutorial('converter');
  }, [startTutorial]);

  const handleCourseClick = useCallback(() => {
    playSound('click');
    window.open('https://www.embroiderytrace.com/help/course/', '_blank', 'noopener,noreferrer');
  }, []);



  // If mobile, render the MobileSidebar component
  if (isMobile) {
    return (
      <MobileSidebar
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onNavigate={onNavigate}
        onResizeImage={onResizeImage}
        onGenerateImage={onGenerateImage}
        setImage64={setImage64}
        setFileName={setFileName}
        onBillingClick={onBillingClick}
      />
    );
  }

  return (
    <>
      {/* Mobile Toggle Button */}
      <button 
        className="sidebar-toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        <IonIcon icon={menuOutline} />
      </button>

      {/* Sidebar */}
      <div 
        className={`sidebar ${isOpen ? 'sidebar-open' : ''} ${isPersistentExpanded ? 'sidebar-persistent-expanded' : ''} ${isCompact ? 'sidebar-compact' : ''} ${features.svgEditor ? '' : 'sidebar--emb'}`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Top Section */}
        <div className="sidebar-content">
          {/* App Logo */}
          <div className="sidebar-logo">
            {features.svgEditor ? (
              <>
                <img 
                  src={TraceLogo} 
                  alt="Trace Logo" 
                  className="sidebar-logo-png" 
                />
                <SVGTraceLogo className="sidebar-logo-svg" />
              </>
            ) : (
              <>
                <img 
                  src={branding.logoIcon} 
                  alt={branding.appName} 
                  className="sidebar-logo-png sidebar-emb-icon"
                />
                <img
                  src={branding.logoDark}
                  alt={branding.appName}
                  className="sidebar-logo-svg sidebar-emb-expanded"
                />
              </>
            )}
          </div>
          
          {sidebarItems.map((item) => (
            <div key={item.id}>
              {item.id === 'create' ? (
                <Upload.Dragger
                  name="file"
                  listType="text"
                  accept="image/jpeg, image/png, image/webp, image/svg+xml, .svg"
                  beforeUpload={handleFileUpload}
                  showUploadList={false}
                  multiple={false}
                  className={`sidebar-upload-dragger ${item.isPrimary ? 'sidebar-item-primary' : ''} ${
                    activeItem === item.id ? 'sidebar-item-active' : ''
                  } ${dragOver ? 'sidebar-upload-dragover' : ''}`}
                  onDrop={() => setDragOver(false)}
                  onDragEnter={() => setDragOver(true)}
                  onDragLeave={() => setDragOver(false)}
                  onClick={() => handleItemClick(item)}
                  data-testid="upload-area"
                  data-onboarding-target="upload"
                  data-tutorial="upload-button"
                >
                  <div className="sidebar-item-icon">
                    {isEmbroideryTrace ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    ) : (
                      <IonIcon icon={item.icon} />
                    )}
                    {item.hasCrown && (
                      <div className="crown-badge">👑</div>
                    )}
                  </div>
                  <span className="sidebar-item-label">{item.label}</span>
                </Upload.Dragger>
              ) : (
                                  <div
                  className={`sidebar-item ${item.isPrimary ? 'sidebar-item-primary' : ''} ${
                    activeItem === item.id ? 'sidebar-item-active' : ''
                  } ${item.id === 'sample' ? 'tutorial-only-button' : ''}`}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={handleItemHover}
                  data-id={item.id}
                  data-onboarding-target={item.id}
                >
                  <div className="sidebar-item-icon">
                    <IonIcon icon={item.icon} />
                    {item.hasCrown && (
                      <div className="crown-badge">👑</div>
                    )}
                  </div>
                  <span className="sidebar-item-label">{item.label}</span>
                </div>
              )}
              
                            {/* Create Actions Submenu */}
              {item.id === 'create' && !isEmbroideryTrace && (
                <div className="create-actions-menu">
                  <Button
                    className="create-action-button"
                    icon={
                      isEmbroideryTrace ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: isPersistentExpanded && !isCompact ? '5px' : undefined }}>
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <IonIcon
                          icon={brush}
                          style={isPersistentExpanded && !isCompact ? { paddingRight: "5px" } : undefined}
                        />
                      )
                    }
                    onClick={() => requirePremium("generate", () => {
                      playSound('click');
                      onGenerateImage();
                    })}
                    onMouseEnter={handleItemHover}
                    style={{
                      background: isEmbroideryTrace
                        ? "linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)"
                        : "#E0E7FF",
                      color: isEmbroideryTrace ? "#ffffff" : "#1E3A8A",
                      border: isEmbroideryTrace ? "none" : "1px solid #1E3A8A",
                      width: "100%",
                      marginBottom: "8px",
                    }}
                    data-action="generate"
                    data-onboarding-target="ai-generate"
                  >
                    Generate
                  </Button>
                  {/* Temporarily hidden: Retrace and Modify entry points.
                  <Button
                    className="create-action-button"
                    icon={
                      <IonIcon
                        icon={refreshOutline}
                        style={isPersistentExpanded && !isCompact ? { paddingRight: "5px" } : undefined}
                      />
                    }
                    onClick={() => requirePremium("retrace", () => {
                      playSound('click');
                      onSharpenImage();
                    })}
                    onMouseEnter={handleItemHover}
                    style={{
                      backgroundColor: isEmbroideryTrace ? "rgba(249, 168, 212, 0.12)" : "#FCE7F3",
                      color: isEmbroideryTrace ? "#f9a8d4" : "#9D174D",
                      border: isEmbroideryTrace ? "1px solid rgba(249, 168, 212, 0.3)" : "1px solid #9D174D",
                      width: "100%",
                      marginBottom: "8px",
                    }}
                  >
                    Retrace
                  </Button>
                  <Button
                    className="create-action-button"
                    data-tutorial="modify-button"
                    icon={
                      <IonIcon
                        icon={colorWandOutline}
                        style={isPersistentExpanded && !isCompact ? { paddingRight: "5px" } : undefined}
                      />
                    }
                    onClick={() => requirePremium("modify", () => {
                      playSound('click');
                      onModifyImage();
                    })}
                    onMouseEnter={handleItemHover}
                    style={{
                      backgroundColor: isEmbroideryTrace ? "rgba(233, 213, 255, 0.12)" : "#F3E8FF",
                      color: isEmbroideryTrace ? "#e9d5ff" : "#6B21A8",
                      border: isEmbroideryTrace ? "1px solid rgba(233, 213, 255, 0.3)" : "1px solid #6B21A8",
                      width: "100%",
                      marginBottom: "8px",
                    }}
                  >
                    Modify
                  </Button>
                  */}
                  
                  {features.svgEditor && (
                  <Button
                    className="create-action-button"
                    icon={
                      <FontAwesomeIcon
                        icon={faExpand}
                        style={isPersistentExpanded && !isCompact ? { paddingRight: "5px" } : undefined}
                      />
                    }
                    onClick={() => requirePremium("resize", () => {
                      playSound('click');
                      onResizeImage();
                    })}
                    onMouseEnter={handleItemHover}
                    style={{
                      backgroundColor: "#B2F5EA",
                      color: "#234E52",
                      border: "1px solid #234E52",
                      width: "100%",
                      marginBottom: "8px",
                    }}
                  >
                    Resize
                  </Button>
                  )}

                  {features.svgEditor && (
                  <Button
                    className="create-action-button"
                    icon={
                      <FontAwesomeIcon
                        icon={faImages}
                        style={isPersistentExpanded && !isCompact ? { paddingRight: "5px" } : undefined}
                      />
                    }
                    onClick={() => requirePremium("bulk-convert", () => {
                      playSound('click');
                      onBulkConvert();
                    })}
                    onMouseEnter={handleItemHover}
                    style={{
                      backgroundColor: "#ffe6f2",
                      color: "#721C24",
                      border: "1px solid #721C24",
                      width: "100%",
                      marginBottom: "8px",
                    }}
                  >
                    Bulk Convert
                  </Button>
                  )}
                  
                </div>
              )}
            </div>
          ))}

        </div>

        {/* Bottom Section */}
        <div className="sidebar-bottom">
          {(features.svgEditor || isEmbroideryTrace) && (
            <div 
              className="sidebar-item" 
              onClick={handleTutorialClick}
              onMouseEnter={handleItemHover}
              data-id="tutorial"
            >
              <div className="sidebar-item-icon">
                <IonIcon icon={schoolOutline} />
              </div>
              <span className="sidebar-item-label">{isEmbroideryTrace ? 'Academy' : 'Tutorials'}</span>
            </div>
          )}

          {isEmbroideryTrace && (
            <div
              className="sidebar-item"
              onClick={handleCourseClick}
              onMouseEnter={handleItemHover}
              data-id="course"
            >
              <div className="sidebar-item-icon">
                <IonIcon icon={playCircleOutline} />
              </div>
              <span className="sidebar-item-label">Video Course</span>
            </div>
          )}
          
          {features.svgEditor ? (
            /* SvgTrace: single Account button with popup */
            <div 
              className="sidebar-item"
              style={{ position: 'relative' }}
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              onMouseEnter={handleItemHover}
              data-id="account"
            >
              <div className="sidebar-item-icon">
                <IonIcon icon={personOutline} />
              </div>
              <span className="sidebar-item-label">Account</span>
              
              {showAccountMenu && (
                <div 
                  className="account-menu-popup"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '0',
                    marginBottom: '8px',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #e2e8f0',
                    minWidth: '200px',
                    overflow: 'hidden',
                    zIndex: 1000,
                  }}
                >
                  <div
                    onClick={() => { setShowAccountMenu(false); setShowSettingsPage(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid #f0f0f0' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <IonIcon icon={shieldCheckmarkOutline} style={{ fontSize: '20px', color: '#667eea' }} />
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#1a202c', fontFamily: 'Clash Grotesk, sans-serif' }}>Settings</span>
                  </div>
                  <div
                    onClick={() => { setShowAccountMenu(false); if (onBillingClick) onBillingClick(); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <IonIcon icon={cardOutline} style={{ fontSize: '20px', color: '#667eea' }} />
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#1a202c', fontFamily: 'Clash Grotesk, sans-serif' }}>My Subscription</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {isSupportOpsAdmin && (
                <div
                  className="sidebar-item"
                  onClick={() => setShowSupportOpsPage(true)}
                  onMouseEnter={handleItemHover}
                  data-id="support-ops"
                >
                  <div className="sidebar-item-icon">
                    <IonIcon icon={settingsOutline} />
                  </div>
                  <span className="sidebar-item-label">Support Ops</span>
                </div>
              )}
              {isEmbroideryTrace && <MonthlyAiUsage />}
              <div
                className="sidebar-item"
                onClick={() => { if (onBillingClick) onBillingClick(); }}
                onMouseEnter={handleItemHover}
                data-id="billing"
              >
                <div className="sidebar-item-icon">
                  <IonIcon icon={cardOutline} />
                </div>
                <span className="sidebar-item-label">Billing</span>
              </div>
            </>
          )}
          
          <div 
            className="sidebar-item" 
            onClick={handleSignOut}
            onMouseEnter={handleItemHover}
            data-id="sign out"
          >
            <div className="sidebar-item-icon">
              {signOutLoading ? (
                <IonSpinner
                  name="lines"
                  style={{
                    width: "16px",
                    height: "16px",
                    color: "#64748b"
                  }}
                />
              ) : (
                <IonIcon icon={logOutOutline} />
              )}
            </div>
            <span className="sidebar-item-label">
              {signOutLoading ? "Signing out..." : "Sign Out"}
            </span>
          </div>
        </div>
      </div>

      {/* Settings Modal (legacy — privacy-only) */}
      <SettingsModal 
        isOpen={showSettingsModal} 
        onClose={() => setShowSettingsModal(false)} 
      />

      {/* Full Settings Page Overlay */}
      {showSettingsPage && (
        <div className="settings-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowSettingsPage(false); }}>
          <SettingsPage onClose={() => setShowSettingsPage(false)} />
        </div>
      )}

      {showSupportOpsPage && (
        <div className="settings-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowSupportOpsPage(false); }}>
          <SupportOpsPage onBack={() => setShowSupportOpsPage(false)} />
        </div>
      )}

      {/* Account menu backdrop */}
      {showAccountMenu && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setShowAccountMenu(false)}
        />
      )}

      {/* Overlay for mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}

      <CancelInterceptModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
      />

      {/* iOS paywall — shown when non-premium user taps a gated feature */}
      {iosPaywallAction && (
        <IOSPaywall
          standalone
          action={iosPaywallAction}
          onClose={() => setIosPaywallAction(null)}
          onSuccess={() => setIosPaywallAction(null)}
        />
      )}
    </>
  );
};

export default Sidebar;
